import { LegacyPaymentsOffer } from '@payments/legacy_payments/types';

export interface PricingComponent {
  name: string;
  priceInCents: number;
  type: string;
}

// LegacyPayments: an offer is made as an agreement, or - while the `purchase_agreements` flag is
// off for the student - as a legacy subscription order. `purchaseAgreements` says which.
export type Offer = PurchaseAgreementOffer | LegacyPaymentsOffer;

export interface PurchaseAgreementOffer {
  nextPath?: string;
  purchaseAgreements: true;
  purchaseAgreementUrl: string;
  purchaseAgreement: PurchaseAgreement;
  paymentDetails: {
    cardBrand: string;
    cardLast4: string;
    cardName: string;
    paymentMethod: PaymentMethod;
    stripePaymentMethodId: string;
    zipCode: string;
  };
  successPath?: string;
}

export interface Pricing {
  components: PricingComponent[];
  couponCode: string;
  installmentTax: number;
  installmentTotal: number;
  listTotal: number;
  renewalTax?: number;
  renewalTotal?: number;
  renewalTotalExcludingTax?: number;
  tax: number;
  total: number;
  totalExcludingTax: number;
  zipCode: string;
}

export interface SubscriptionPlan {
  currency: string;
  description: string;
  durationDays: number;
  durationMonths: number;
  durationYears: number;
  id: number;
  initialAmount?: number;
  installmentInterval?: 'week' | 'month';
  installmentIntervalCount?: number;
  name: string;
  planSlug: string;
  priceCents: number;
  recurring: boolean;
  slug: string;
}

export interface PurchaseAgreement {
  allowInstallments: boolean;
  isUpgrade: boolean;
  pricing: Pricing;
  replacedAgreement?: ReplacedAgreement;
  stripeToken?: string;
  subscriptionPlan: SubscriptionPlan;
}

export interface ReplacedAgreement {
  displayName: string;
}

export interface BulkSelfServeFormProps {
  isEuUser: boolean;
  nonprofit: boolean;
  signedIn: boolean;
  stripeToken: string;
  subscriptionPlans: SubscriptionPlan[];
}

export interface PurchaseAgreementFormProps {
  isEuUser: boolean;
  purchaseAgreement: PurchaseAgreement;
  signedIn: boolean;
}

export interface UserFormValues {
  email?: string;
  name?: string;
  password?: string;
  passwordConfirmation?: string;
}

export interface CreatePurchaseAgreementParams {
  authenticityToken?: string;
  billingCycle: BillingCycle;
  couponCode?: string;
  paymentMethod?: PaymentMethod;
  stripePaymentMethodId?: string;
  subscriptionPlanId: number;
  user?: UserFormValues;
  zipCode?: string;
}

export interface PurchaseAgreementFormValues extends CreatePurchaseAgreementParams {
  _error?: string;
  billingCycle: BillingCycle;
  paymentMethod: PaymentMethod;
  user: UserFormValues;

  // UI-only
  cardName?: string;
  pricing: Pricing;
  signedIn: boolean;
  termsAndPolicyAccepted: boolean;
}

export enum BillingCycle {
  INSTALLMENTS = 'installments',
  ONE_TIME = 'one_time',
  RECURRING = 'recurring'
}

export enum PaymentMethod {
  ALIPAY = 'stripe_alipay',
  PAYPAL = 'paypal_express',
  STRIPE = 'stripe',
  STRIPE_PAYMENT_REQUEST = 'stripe_payment_request'
}

export interface CreatePurchaseAgreementResponse {
  actionRequired?: {
    clientSecret: string;
    paymentMethodId?: string;
    type: 'confirmSetup' | 'confirmPayment' | 'offsiteConfirmation';
  };
  nextUrl?: string;
}
